import { ROOT_NETS } from '@somnhold/content';
import { hexDist, hexLen, hexLine, hexToWorld, ROW, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';
import type { RootsState, RootTip } from '../types.ts';

export type { RootsState, RootTip };

/**
 * The Circle's wood under the Grandsire (docs/sendas/druida.md, chapter 1, spring): the grounds, the same from the seed
 * in the sim and the drawing, integers only. Up is −y (the top of the screen).
 *
 * - `thawLayout` (act 1, the thaw): a slope of wood four or five hexes wide winding up from the foot of the board to
 *   Olmo's clearing at its top; trunks here and there (they can't be walked), the mounds (lomas) every few rows where
 *   a surge of melt water splits round, the mud (barro) that slows.
 * - `gladeLayout` (act 2, the face in the bark): Olmo's clearing, round, the flood all round it; the old trunk at its
 *   north where Ceda looks for the face, the new shoots (brotes) in a ring, the mud pools at its rim.
 * - `streamLayout` (act 3, the test, «El puente de raíces»): the swollen stream across the board, the bank at its foot
 *   with the two living roots, the islet with the seed in its middle, the stones that show (a root net, from
 *   content ROOT_NETS, mirrored by the seed); two mounds on the bank.
 *
 * And the roots' rule (§4, puzzle 1), pure, for the sim and its solver: a root grows toward the nearest sprout it sees
 * (`see` hexes or less, only forward, never back to the bank) and hasn't reached, a hex every `per` ticks, only over
 * stone (it crosses one hex of water at most between two stones); it waits if it sees none. At a sprout it holds and
 * goes on to the next it sees. The islet's seed is a sprout too. Two roots that reach the same sprout less than `near`
 * apart tangle and stop; one that comes `near` or more after the other braids with it and follows it. The bridge wants
 * both roots on the islet. (The islet never tangles: it is big enough for both, so a cut can never leave them stuck.)
 */

export const THAW_RADIUS = 13;
export const GLADE_RADIUS = 7;
export const STREAM_RADIUS = 8;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;
const key = (h: Hex): string => `${h.q},${h.r}`;
/** The column of a point's hex on row r, rounded to the nearest (integers only). */
const qOf = (x: number, r: number): number => {
  const v = x - r * 500;
  return v >= 0 ? idiv(v + 500, 1000) : -idiv(-v + 499, 1000);
};

/* ---------- the roots ---------- */

/** A root net: node 0 the left root, 1 the right one (on the bank), 2 the islet, 3… the stones; `edge[a][b]` the hexes a root grows from a to b (0: it can't). */
export interface RootNet {
  nodes: Hex[];
  edge: number[][];
}

/** The rule's numbers (content DRUID_PATH.roots): ticks a hex, how far a root sees, how near two arrivals tangle. */
export interface RootParams {
  per: number;
  see: number;
  near: number;
}

export type RootEvent = { k: 'grow' | 'reach' | 'tangle' | 'braid' | 'home' | 'back'; root: number; node: number };

/** The net with its edges: within `see`, forward only (toward the islet), over stone with one hex of water at most between. */
export const rootNet = (nodes: Hex[], see: number): RootNet => {
  const stone = new Set(nodes.map(key));
  const edge = nodes.map((a, i) =>
    nodes.map((b, j) => {
      if (i === j || j < 2 || b.r >= a.r) return 0;
      const d = hexDist(a, b);
      if (d > see) return 0;
      const line = hexLine(a, b);
      let wet = 0;
      for (let k = 1; k < line.length - 1; k++) {
        if (stone.has(key(line[k] as Hex))) wet = 0;
        else if (++wet > 1) return 0;
      }
      return d;
    }),
  );
  return { nodes, edge };
};

export const rootsFresh = (net: RootNet): RootsState => ({
  tips: [0, 1].map((n) => ({ at: n, prev: n, to: -1, t0: 0, arr: 0, mode: 0, follow: -1, trail: [n] })),
  sprout: 1 << 2,
  hit: net.nodes.flatMap(() => [-1, -1]),
  knot: -1,
});

export const rootsClone = (st: RootsState): RootsState => ({ tips: st.tips.map((t) => ({ ...t, trail: [...t.trail] })), sprout: st.sprout, hit: [...st.hit], knot: st.knot });

/** The node a waiting root grows to next (−1: none it sees): its leader's next if it braided, else the nearest sprout ahead. */
const rootTarget = (net: RootNet, st: RootsState, i: number): number => {
  const tip = st.tips[i] as RootTip;
  if (tip.follow >= 0) {
    const L = (st.tips[tip.follow] as RootTip).trail;
    const k = L.indexOf(tip.at);
    return k >= 0 && k + 1 < L.length ? (L[k + 1] as number) : -1;
  }
  const from = net.edge[tip.at] as number[];
  let best = -1;
  let bl = 99;
  for (let n = 2; n < net.nodes.length; n++) {
    const len = from[n] as number;
    if (!len || !(st.sprout & (1 << n)) || tip.trail.includes(n)) continue;
    if (len < bl) {
      bl = len;
      best = n;
    }
  }
  return best;
};

const retarget = (net: RootNet, P: RootParams, st: RootsState, i: number, t: number, out?: RootEvent[]): void => {
  const tip = st.tips[i] as RootTip;
  if (tip.mode !== 0) return;
  const n = rootTarget(net, st, i);
  if (n < 0) return;
  tip.to = n;
  tip.t0 = t;
  tip.arr = t + P.per * ((net.edge[tip.at] as number[])[n] as number);
  tip.mode = 1;
  out?.push({ k: 'grow', root: i, node: n });
};

const arrive = (P: RootParams, st: RootsState, i: number, out?: RootEvent[]): void => {
  const tip = st.tips[i] as RootTip;
  const n = tip.to;
  tip.prev = tip.at;
  tip.at = n;
  tip.to = -1;
  tip.mode = 0;
  tip.trail.push(n);
  st.hit[n * 2 + i] = tip.arr;
  if (n === 2) {
    tip.mode = 3;
    out?.push({ k: 'home', root: i, node: n });
    return;
  }
  out?.push({ k: 'reach', root: i, node: n });
  const j = 1 - i;
  const o = st.tips[j] as RootTip;
  const th = st.hit[n * 2 + j] as number;
  if (th < 0 || o.follow === i || tip.follow === j) return;
  if (tip.arr - th < P.near) {
    // Tangled: both stop at the sprout (the other back to it if it already grew on).
    o.mode = 2;
    o.to = -1;
    tip.mode = 2;
    st.knot = n;
    out?.push({ k: 'tangle', root: i, node: n });
  } else {
    tip.follow = j;
    out?.push({ k: 'braid', root: i, node: n });
  }
};

/** Everything due up to tick `t`: the roots that get somewhere (in order), then the waiting ones look again. */
export const rootsAdvance = (net: RootNet, P: RootParams, st: RootsState, t: number, out?: RootEvent[]): void => {
  for (let guard = 0; guard < 16; guard++) {
    let i = -1;
    for (let k = 0; k < st.tips.length; k++) {
      const tip = st.tips[k] as RootTip;
      if (tip.mode === 1 && tip.arr <= t && (i < 0 || tip.arr < (st.tips[i] as RootTip).arr)) i = k;
    }
    if (i < 0) break;
    const when = (st.tips[i] as RootTip).arr;
    arrive(P, st, i, out);
    retarget(net, P, st, i, when, out);
  }
  for (let k = 0; k < st.tips.length; k++) retarget(net, P, st, k, t, out);
};

/** A seed sown on a stone (node): its sprout. */
export const rootsSow = (st: RootsState, n: number): void => {
  st.sprout |= 1 << n;
};

/** A sprout lost (eaten, or its knot cut): who grew toward it on its own stops where it holds. */
export const rootsLose = (st: RootsState, n: number): void => {
  if (n === 2) return;
  st.sprout &= ~(1 << n);
  for (const tip of st.tips) {
    if (tip.mode === 1 && tip.to === n && tip.follow < 0) {
      tip.mode = 0;
      tip.to = -1;
    }
  }
};

/**
 * The knot cut: the two roots it tied wither back to the bank, and the sprouts they held die with them (the islet's
 * stays). Back a stone only, they could be left as far from the stone they share as each other, and then every sowing
 * tangles them again: the bridge stuck for good. From the bank there is always a sowing that parts them; it costs seeds.
 */
export const rootsCut = (st: RootsState, out?: RootEvent[]): void => {
  const n = st.knot;
  if (n < 0) return;
  st.tips.forEach((tip, i) => {
    if (tip.mode !== 2) return;
    for (const k of tip.trail) {
      if (k > 2) st.sprout &= ~(1 << k);
      st.hit[k * 2 + i] = -1;
    }
    tip.trail = [i];
    tip.at = i;
    tip.prev = i;
    tip.mode = 0;
    tip.to = -1;
    tip.follow = -1;
    out?.push({ k: 'back', root: i, node: i });
  });
  for (const tip of st.tips) if (tip.follow >= 0 && (st.tips[tip.follow] as RootTip).trail.length === 1) tip.follow = -1;
  st.sprout &= ~(1 << n);
  st.knot = -1;
};

export const rootsWon = (st: RootsState): boolean => st.tips.every((t) => t.mode === 3);

/** A sowing plan played out on paper (no knots cut, nothing eaten): won, when, and whether anything tangled. */
export const rootsPlay = (net: RootNet, P: RootParams, sows: { n: number; t: number }[], horizon: number, from?: RootsState, now = 0): { won: boolean; t: number; tangled: boolean } => {
  const st = from ? rootsClone(from) : rootsFresh(net);
  const plan = [...sows].sort((a, b) => a.t - b.t || a.n - b.n);
  for (const s of plan) {
    rootsAdvance(net, P, st, s.t);
    if (st.knot >= 0) return { won: false, t: s.t, tangled: true };
    rootsSow(st, s.n);
    rootsAdvance(net, P, st, s.t);
  }
  // On to the horizon, event by event.
  for (let guard = 0; guard < 40; guard++) {
    if (st.knot >= 0) return { won: false, t: 0, tangled: true };
    if (rootsWon(st)) return { won: true, t: st.tips.reduce((m, x) => imax(m, x.arr), 0), tangled: false };
    let next = -1;
    for (const tip of st.tips) if (tip.mode === 1 && (next < 0 || tip.arr < next)) next = tip.arr;
    if (next < 0 || next > horizon) break;
    rootsAdvance(net, P, st, next);
  }
  void now;
  return { won: rootsWon(st), t: horizon, tangled: st.knot >= 0 };
};

/** Every sowing plan of up to `most` seeds on the stones not sprouted, at `step`·k ticks from `t0` (k < `slots`): the ones that win. */
export const rootsSolve = (
  net: RootNet,
  P: RootParams,
  most: number,
  slots: number,
  o: { from?: RootsState; t0?: number; skip?: number; limit?: number } = {},
): { sows: { n: number; t: number }[]; t: number }[] => {
  const t0 = o.t0 ?? 0;
  const from = o.from;
  const free: number[] = [];
  for (let n = 3; n < net.nodes.length; n++) if (!((from?.sprout ?? 0) & (1 << n)) && !((o.skip ?? 0) & (1 << n))) free.push(n);
  const wins: { sows: { n: number; t: number }[]; t: number }[] = [];
  const horizon = t0 + P.per * 40;
  // Already won with nothing more sown?
  if (from && rootsPlay(net, P, [], horizon, from).won) return [{ sows: [], t: t0 }];
  const pick: number[] = [];
  const at: number[] = [];
  const rec = (start: number): void => {
    if (o.limit && wins.length >= o.limit) return;
    if (pick.length) {
      const sows = pick.map((n, k) => ({ n, t: t0 + (at[k] as number) * P.per }));
      const r = rootsPlay(net, P, sows, horizon, from);
      if (r.won) {
        wins.push({ sows, t: r.t });
        return;
      }
    }
    if (pick.length >= most) return;
    for (let k = start; k < free.length; k++) {
      for (let s = 0; s < slots; s++) {
        pick.push(free[k] as number);
        at.push(s);
        rec(k + 1);
        pick.pop();
        at.pop();
      }
    }
  };
  rec(0);
  return wins;
};

/** The net of a fight: one of content ROOT_NETS by the seed, or its mirror (left and right swapped). */
export interface StreamLayout {
  radius: number;
  rocks: Hex[];
  /** For the drawing: the water's cells (the stream), the far bank's, the bank's. */
  water: Hex[];
  far: Hex[];
  bank: Hex[];
  /** The bank's two mounds (a muddy wave of the Lodazal doesn't reach them), each its cells. */
  mounds: Hex[][];
  net: RootNet;
  /** The net's index in ROOT_NETS and whether it is mirrored. */
  pick: number;
  mirror: boolean;
  start: { x: number; y: number };
  ceda: { x: number; y: number };
  /** Where the Lodazal and the slimes come up out of the water onto the bank (the water's edge, sim points). */
  edge: { x: number; y: number }[];
}

/** A net's nodes from its row of content numbers ([q, r] for the left root, the right one, the islet, then the stones). */
export const netNodes = (row: readonly number[], mirror: boolean): Hex[] => {
  const out: Hex[] = [];
  for (let k = 0; k + 1 < row.length; k += 2) {
    const q = row[k] as number;
    const r = row[k + 1] as number;
    out.push(mirror ? { q: -q - r, r } : { q, r });
  }
  // Mirrored, the left root is the other one.
  if (mirror) {
    const a = out[0] as Hex;
    out[0] = out[1] as Hex;
    out[1] = a;
  }
  return out;
};

export const streamLayout = (seed: number, see: number): StreamLayout => {
  const R = STREAM_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x57e4));
  const pick = rngInt(rng, ROOT_NETS.length);
  const mirror = rngInt(rng, 2) === 1;
  const nodes = netNodes(ROOT_NETS[pick] as readonly number[], mirror);
  const net = rootNet(nodes, see);
  const bankRow = imax((nodes[0] as Hex).r, (nodes[1] as Hex).r);
  const isle = nodes[2] as Hex;
  const stones = new Set(nodes.slice(2).map(key));
  const rocks: Hex[] = [];
  const water: Hex[] = [];
  const far: Hex[] = [];
  const bank: Hex[] = [];
  const mounds: Hex[][] = [[], []];
  for (const h of allCells(R)) {
    if (h.r >= bankRow) {
      bank.push(h);
      continue;
    }
    // The islet: its seed's cell and a ring round it, low; the stones show; the rest is water, the far bank past it.
    if (stones.has(key(h))) continue;
    if (h.r < isle.r - 1) far.push(h);
    else water.push(h);
    rocks.push(h);
  }
  // The bank's mounds: a cell and its neighbour, two rows in, left and right of the middle.
  const mr = bankRow + 2;
  for (const [k, side] of [
    [0, -1],
    [1, 1],
  ] as const) {
    const x = side * (3000 + rngInt(rng, 2) * 1000);
    const q = qOf(x - (mr % 2 === 0 ? 0 : 500), mr);
    const a = { q, r: mr };
    (mounds[k] as Hex[]).push(a, { q: a.q + 1, r: a.r });
  }
  // The water's edge along the bank (the row just above it): where things come up.
  const edge: { x: number; y: number }[] = [];
  for (const h of water) if (h.r === bankRow - 1 && hexLen(h.q, h.r) <= R - 1 && !stones.has(key(h))) edge.push(hexToWorld(h.q, h.r));
  edge.sort((a, b) => a.x - b.x);
  rocks.sort(sortHex);
  water.sort(sortHex);
  far.sort(sortHex);
  bank.sort(sortHex);
  return {
    radius: R,
    rocks,
    water,
    far,
    bank,
    mounds,
    net,
    pick,
    mirror,
    start: { x: 0, y: (bankRow + 2) * ROW },
    ceda: { x: (mirror ? 1 : -1) * 4500, y: (bankRow + 3) * ROW },
    edge,
  };
};

/* ---------- the thaw ---------- */

export interface ThawLayout {
  radius: number;
  rocks: Hex[];
  /** For the drawing: the wood's floor (open), the trunks standing in it (rocks inside the wood), the wood beyond (rocks). */
  floor: Hex[];
  trunks: Hex[];
  /** The mounds (each its cells, from the foot up), the mud (each patch its cells). */
  mounds: Hex[][];
  mud: Hex[][];
  /** The middle of the way, row by row from the foot to the clearing (sim points). */
  way: { x: number; y: number }[];
  /** Olmo's clearing at the top: its middle, and the row a surge comes down from (y). */
  glade: { x: number; y: number };
  top: number;
  foot: number;
  start: { x: number; y: number };
}

export const thawLayout = (seed: number): ThawLayout => {
  const R = THAW_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x7a3e));
  // The way's middle on each row, from the foot (r = R − 1) up to the clearing (r = −(R − 1)): it winds.
  const mid = new Map<number, number>();
  let x = rngRange(rng, -2, 2) * 500;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = R - 1; r >= -(R - 1); r--) {
    mid.set(r, x);
    if (rngInt(rng, 10) < 3) dir = -dir;
    x = imax(-4000, imin(4000, x + dir * 500));
    if (Math.abs(x) === 4000) dir = -dir;
  }
  const gladeRow = -(R - 3);
  const half = (r: number): number => (r <= gladeRow + 1 ? 3200 : 2100);
  // The mounds: every five rows from the foot, on one side then the other, two cells wide.
  const moundAt = new Map<string, number>();
  const mounds: Hex[][] = [];
  let side = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = R - 4; r > gladeRow + 2; r -= rngRange(rng, 4, 5)) {
    const m = mid.get(r) as number;
    const cx = m + side * 1000;
    const q = qOf(cx, r);
    const cells = [
      { q, r },
      { q: q + (side > 0 ? -1 : 1), r },
      { q: side > 0 ? q : q, r: r - 1 },
    ].filter((h) => Math.abs(hexToWorld(h.q, h.r).x - (mid.get(h.r) ?? 0)) <= half(h.r));
    cells.forEach((h) => moundAt.set(key(h), mounds.length));
    mounds.push(cells);
    side = -side;
  }
  // The mud: a patch between two mounds, across the way.
  const mudAt = new Set<string>();
  const mud: Hex[][] = [];
  for (let r = R - 5; r > gladeRow + 2; r -= rngRange(rng, 4, 6)) {
    const m = mid.get(r) as number;
    const cells: Hex[] = [];
    for (const dr of [0, -1]) {
      const rr = r + dr;
      const mm = mid.get(rr) as number;
      for (let dx = -1500; dx <= 1500; dx += 1000) {
        const q = qOf(mm + dx + rngRange(rng, 0, 1) * 500, rr);
        const h = { q, r: rr };
        if (moundAt.has(key(h)) || mudAt.has(key(h))) continue;
        if (Math.abs(hexToWorld(q, rr).x - mm) > half(rr)) continue;
        mudAt.add(key(h));
        cells.push(h);
      }
    }
    void m;
    if (cells.length) mud.push(cells);
  }
  const rocks: Hex[] = [];
  const floor: Hex[] = [];
  const trunks: Hex[] = [];
  for (const h of allCells(R)) {
    const m = mid.get(h.r);
    const p = hexToWorld(h.q, h.r);
    if (m === undefined || Math.abs(p.x - m) > half(h.r)) {
      rocks.push(h);
      continue;
    }
    // A trunk now and then, never on the way's middle, a mound, the mud or in the clearing.
    const k = key(h);
    const onMid = Math.abs(p.x - m) <= 500;
    if (!onMid && !moundAt.has(k) && !mudAt.has(k) && h.r > gladeRow + 1 && h.r < R - 2 && rngInt(rng, 100) < 14) {
      trunks.push(h);
      rocks.push(h);
      continue;
    }
    floor.push(h);
  }
  const way: { x: number; y: number }[] = [];
  for (let r = R - 1; r >= gladeRow; r--) way.push({ x: mid.get(r) as number, y: r * ROW });
  rocks.sort(sortHex);
  floor.sort(sortHex);
  trunks.sort(sortHex);
  const g = way[way.length - 1] as { x: number; y: number };
  return {
    radius: R,
    rocks,
    floor,
    trunks,
    mounds,
    mud,
    way,
    glade: { x: g.x, y: (gladeRow - 1) * ROW },
    top: (gladeRow + 2) * ROW,
    foot: (R - 1) * ROW + 600,
    start: { x: (way[0] as { x: number }).x, y: (R - 1) * ROW },
  };
};

/* ---------- Olmo's clearing ---------- */

export interface GladeLayout {
  radius: number;
  rocks: Hex[];
  /** For the drawing: the flood round the clearing, the old trunk's cells. */
  flood: Hex[];
  trunk: Hex[];
  /** The shoots (brotes), in the order Hob fences them (the nearest to him first, then on round), and the mud pools at the rim. */
  shoots: Hex[];
  pools: Hex[];
  ceda: { x: number; y: number };
  hob: { x: number; y: number };
  start: { x: number; y: number };
}

export const gladeLayout = (seed: number, shoots: number): GladeLayout => {
  const R = GLADE_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x61ad));
  const trunk: Hex[] = [
    { q: 0, r: -5 },
    { q: 1, r: -5 },
    { q: -1, r: -4 },
    { q: 0, r: -4 },
    { q: 1, r: -6 },
  ];
  const trunkAt = new Set(trunk.map(key));
  const rocks: Hex[] = [];
  const flood: Hex[] = [];
  for (const h of allCells(R + 2)) {
    if (hexLen(h.q, h.r) > R - 1) {
      flood.push(h);
      rocks.push(h);
    } else if (trunkAt.has(key(h))) rocks.push(h);
  }
  // The shoots: a ring two to four hexes out, two apart at least, none by the trunk.
  const cand: Hex[] = [];
  for (const h of allCells(4)) {
    const d = hexLen(h.q, h.r);
    if (d < 2 || trunk.some((t) => hexDist(t, h) < 3)) continue;
    cand.push(h);
  }
  const out: Hex[] = [];
  for (let guard = 0; out.length < shoots && guard < 400; guard++) {
    const h = cand[rngInt(rng, cand.length)] as Hex;
    if (out.some((o) => hexDist(o, h) < 2)) continue;
    out.push(h);
  }
  // Hob's order: from the one nearest him (south of the middle), each next the nearest left.
  const order: Hex[] = [];
  let at: Hex = { q: 0, r: 1 };
  const left = [...out];
  while (left.length) {
    let bi = 0;
    for (let i = 1; i < left.length; i++) if (hexDist(left[i] as Hex, at) < hexDist(left[bi] as Hex, at)) bi = i;
    at = left.splice(bi, 1)[0] as Hex;
    order.push(at);
  }
  // The mud pools at the rim, a sixth of a turn apart at least.
  const ring: Hex[] = [];
  for (const h of allCells(R - 1)) if (hexLen(h.q, h.r) === R - 1 && !trunk.some((t) => hexDist(t, h) < 3)) ring.push(h);
  ring.sort(sortHex);
  const pools: Hex[] = [];
  for (let guard = 0; pools.length < 4 && guard < 300; guard++) {
    const h = ring[rngInt(rng, ring.length)] as Hex;
    if (pools.some((o) => hexDist(o, h) < 5)) continue;
    pools.push(h);
  }
  rocks.sort(sortHex);
  flood.sort(sortHex);
  pools.sort(sortHex);
  return { radius: R, rocks, flood, trunk, shoots: order, pools, ceda: hexToWorld(0, -3), hob: hexToWorld(0, 1), start: hexToWorld(0, 2) };
};
